import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  AssignableCourier,
  AssignmentPreview,
  CustomerOrder,
  DeliveryJob,
  DeliveryJobEvent,
  DeliveryJobStatus,
  Location,
} from "../api/types";
import { Modal } from "../components/Modal";
import { ReasonModal } from "../components/ui/ReasonModal";
import { RecordPaymentModal } from "../components/orders/RecordPaymentModal";
import { orderDayKey } from "../components/orders/orderUi";
import { useAuth } from "../state/AuthContext";

const OPEN_STATUSES: DeliveryJobStatus[] = ["PENDING", "ASSIGNED", "PICKED_UP"];

const STATUS_LABELS: Record<DeliveryJobStatus, string> = {
  PENDING: "Pending",
  ASSIGNED: "Assigned",
  PICKED_UP: "Picked up",
  DELIVERED: "Delivered",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

function jobStatusClass(status: DeliveryJobStatus): string {
  switch (status) {
    case "PENDING":
      return "badge muted";
    case "ASSIGNED":
      return "badge primary";
    case "PICKED_UP":
      return "badge warning";
    case "DELIVERED":
      return "badge success";
    default:
      return "badge danger";
  }
}

export function DispatchPage() {
  const { hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState<DeliveryJob[]>([]);
  const [couriers, setCouriers] = useState<AssignableCourier[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(searchParams.has("orderId"));
  const [assigningJob, setAssigningJob] = useState<DeliveryJob | null>(null);
  const [historyJob, setHistoryJob] = useState<DeliveryJob | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = new Date();
      from.setDate(from.getDate() - 30);
      const to = new Date();
      to.setDate(to.getDate() + 30);
      const [jobList, courierList, locationList] = await Promise.allSettled([
        api.listDeliveryJobs({ from: orderDayKey(from), to: orderDayKey(to) }),
        api.listAssignableCouriers(),
        api.listLocations(),
      ]);
      if (jobList.status === "fulfilled") setJobs(jobList.value);
      if (courierList.status === "fulfilled") setCouriers(courierList.value);
      if (locationList.status === "fulfilled") {
        setLocations(locationList.value.filter((loc) => loc.isActive));
      }
      const failed = [jobList, courierList, locationList].find(
        (result) => result.status === "rejected",
      );
      if (failed?.status === "rejected") {
        setError(
          failed.reason instanceof Error
            ? failed.reason.message
            : "Could not load dispatch data.",
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load dispatch data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openJobs = useMemo(
    () => jobs.filter((job) => OPEN_STATUSES.includes(job.status)),
    [jobs],
  );
  const closedJobs = useMemo(
    () => jobs.filter((job) => !OPEN_STATUSES.includes(job.status)).slice(-20),
    [jobs],
  );

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Courier board</h2>
          <p className="page-subtitle">
            Courier delivery jobs — create, assign, and track payment.
          </p>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {hasRole("OWNER") ? (
            <Link to="/app/payout-rules" className="button plain">
              Payout rules
            </Link>
          ) : null}
          <button type="button" className="button" onClick={() => setCreating(true)}>
            New delivery
          </button>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {loading ? <p className="muted">Loading…</p> : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(20rem, 1fr))",
          gap: "var(--space-4)",
          alignItems: "start",
        }}
      >
        {OPEN_STATUSES.map((status) => (
          <div key={status} className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <div className="day-shifts-row-head">
              <h3 style={{ margin: 0 }}>{STATUS_LABELS[status]}</h3>
              <span className="badge muted">
                {openJobs.filter((job) => job.status === status).length}
              </span>
            </div>
            {openJobs
              .filter((job) => job.status === status)
              .map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onAssign={() => setAssigningJob(job)}
                  onHistory={() => setHistoryJob(job)}
                  onChanged={refresh}
                  onError={setError}
                />
              ))}
            {openJobs.filter((job) => job.status === status).length === 0 && !loading ? (
              <p className="muted">Nothing here.</p>
            ) : null}
          </div>
        ))}
      </div>

      {closedJobs.length > 0 ? (
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <h3 style={{ margin: 0 }}>Recently closed</h3>
          {closedJobs.map((job) => (
            <div key={job.id} className="day-shifts-row">
              <div className="day-shifts-row-head">
                <div>
                  <strong>{job.code}</strong> · {job.recipientName} ·{" "}
                  {job.deliveryAddress}
                </div>
                <span className={jobStatusClass(job.status)}>
                  {STATUS_LABELS[job.status]}
                </span>
              </div>
              <p className="muted" style={{ margin: 0 }}>
                {job.priceEur} € · courier {job.courier?.fullName ?? "—"}
                {job.courierPayoutEur ? ` · payout ${job.courierPayoutEur} €` : ""}
                {job.status === "DELIVERED" && !job.paidToCourier ? " · payout unpaid" : ""}
              </p>
              <div className="button-row">
                {job.status === "DELIVERED" && !job.paidToCourier ? (
                  <button
                    type="button"
                    className="button plain"
                    onClick={() =>
                      void api
                        .markDeliveryJobPaidToCourier(job.id)
                        .then(refresh)
                        .catch((err: unknown) =>
                          setError(err instanceof Error ? err.message : "Failed"),
                        )
                    }
                  >
                    Mark paid to courier
                  </button>
                ) : null}
                <button
                  type="button"
                  className="button plain"
                  onClick={() => setHistoryJob(job)}
                >
                  History
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {creating ? (
        <NewDeliveryModal
          locations={locations}
          prefillOrderId={searchParams.get("orderId")}
          onClose={() => {
            setCreating(false);
            if (searchParams.has("orderId")) setSearchParams({}, { replace: true });
          }}
          onCreated={async () => {
            setCreating(false);
            if (searchParams.has("orderId")) setSearchParams({}, { replace: true });
            await refresh();
          }}
        />
      ) : null}

      {assigningJob ? (
        <AssignModal
          job={assigningJob}
          couriers={couriers}
          onClose={() => setAssigningJob(null)}
          onAssigned={async () => {
            setAssigningJob(null);
            await refresh();
          }}
        />
      ) : null}

      {historyJob ? (
        <JobHistoryModal job={historyJob} onClose={() => setHistoryJob(null)} />
      ) : null}
    </section>
  );
}

function JobCard({
  job,
  onAssign,
  onHistory,
  onChanged,
  onError,
}: {
  job: DeliveryJob;
  onAssign: () => void;
  onHistory: () => void;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    onError(null);
    try {
      await action();
      await onChanged();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Could not update the job.");
    } finally {
      setBusy(false);
    }
  };

  // D-11: kit ReasonModal instead of the native browser dialog.
  const [cancelOpen, setCancelOpen] = useState(false);

  // OD-101: nothing leaves the shop while money is owed — the ORDER's own
  // Record payment modal (loaded on demand: the job carries a summary).
  const [paymentOrder, setPaymentOrder] = useState<CustomerOrder | null>(null);
  const openPayment = async () => {
    if (!job.order) return;
    setBusy(true);
    onError(null);
    try {
      setPaymentOrder(await api.getCustomerOrder(job.order.id));
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Could not load the order.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (reason: string | undefined) => {
    setCancelOpen(false);
    await run(() => api.cancelDeliveryJob(job.id, { reason }));
  };

  return (
    <div className="day-shifts-row">
      <div className="day-shifts-row-head">
        <div>
          <strong>{job.code}</strong>
          {job.order ? <span className="muted"> · {job.order.code}</span> : null}
          {/* G-21 amendment (OD-28): the two handover witnesses may disagree —
              courier claimed pickup, shop silent. Surface it, never block. */}
          {job.status === "PICKED_UP" && !job.handoverConfirmedAt ? (
            <span className="badge warning" style={{ marginLeft: 6 }}>
              handover unconfirmed by shop
            </span>
          ) : null}
          {job.handoverConfirmedAt ? (
            <span className="badge success" style={{ marginLeft: 6 }}>
              handover confirmed
              {job.handoverConfirmedBy ? ` · ${job.handoverConfirmedBy.fullName}` : ""}
            </span>
          ) : null}
        </div>
        <span className="muted">
          {new Date(job.scheduledDate).toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
          })}
        </span>
      </div>
      <p style={{ margin: 0 }}>
        {job.recipientName} · {job.deliveryAddress}
      </p>
      <p className="muted" style={{ margin: 0 }}>
        {job.priceEur} €{job.distanceKm ? ` · ${job.distanceKm} km` : ""}
        {job.courier ? ` · ${job.courier.fullName}` : ""}
        {job.courierPayoutEur ? ` · payout ${job.courierPayoutEur} €` : ""}
        {job.paidByClient ? " · client paid" : ""}
      </p>
      <div className="button-row">
        {job.status === "PENDING" ? (
          <button type="button" className="button" onClick={onAssign} disabled={busy}>
            Assign courier
          </button>
        ) : null}
        {job.status === "ASSIGNED" ? (
          <button
            type="button"
            className="button plain"
            onClick={() => void run(() => api.unassignDeliveryJob(job.id))}
            disabled={busy}
          >
            Unassign
          </button>
        ) : null}
        {/* OD-100: an order's fee is paid through the ORDER's ledger — the
            bare flag is only for orderless (and legacy walk-in) jobs. */}
        {!job.paidByClient && !job.feePaidThroughOrder ? (
          <button
            type="button"
            className="button plain"
            onClick={() => void run(() => api.markDeliveryJobPaidByClient(job.id))}
            disabled={busy}
          >
            Client paid
          </button>
        ) : null}
        {job.feePaidThroughOrder && job.order?.balanceDueEur &&
        Number(job.order.balanceDueEur) > 0 ? (
          <button
            type="button"
            className="button plain"
            onClick={() => void openPayment()}
            disabled={busy}
          >
            Record payment · {job.order.balanceDueEur} € owed
          </button>
        ) : null}
        {job.status === "PENDING" || job.status === "ASSIGNED" ? (
          <button
            type="button"
            className="button danger"
            onClick={() => setCancelOpen(true)}
            disabled={busy}
          >
            Cancel
          </button>
        ) : null}
        <button type="button" className="button plain" onClick={onHistory} disabled={busy}>
          History
        </button>
      </div>

      {paymentOrder ? (
        <RecordPaymentModal
          order={paymentOrder}
          onClose={() => setPaymentOrder(null)}
          onSaved={async () => {
            setPaymentOrder(null);
            await onChanged();
          }}
        />
      ) : null}

      {cancelOpen ? (
        <ReasonModal
          title={`Cancel delivery ${job.code}?`}
          message="The job is marked cancelled; the reason is kept on its event log."
          reasonMode="optional"
          confirmLabel="Cancel delivery"
          cancelLabel="Keep delivery"
          destructive
          busy={busy}
          onConfirm={(reason) => void cancel(reason)}
          onClose={() => setCancelOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * D-6: the job's audited timeline — arrival/delivery distances, out-of-radius
 * overrides, floor confirmations. Makes geo flags visible to dispatch instead
 * of write-only rows in the database.
 */
function JobHistoryModal({ job, onClose }: { job: DeliveryJob; onClose: () => void }) {
  const [events, setEvents] = useState<DeliveryJobEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    void api
      .listDeliveryJobEvents(job.id)
      .then((rows) => {
        if (!stale) setEvents(rows);
      })
      .catch((err: unknown) => {
        if (!stale) setError(err instanceof Error ? err.message : "Could not load history.");
      });
    return () => {
      stale = true;
    };
  }, [job.id]);

  return (
    <Modal title={`History · ${job.code}`} onClose={onClose}>
      {error ? <div className="alert error">{error}</div> : null}
      {!events && !error ? <p className="muted">Loading…</p> : null}
      {events?.length === 0 ? <p className="muted">No events recorded.</p> : null}
      {events?.map((event) => (
        <div key={event.id} className="day-shifts-row">
          <div className="day-shifts-row-head">
            <span>
              {new Date(event.createdAt).toLocaleString(undefined, {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
              {event.actor ? ` · ${event.actor.fullName}` : ""}
            </span>
            {event.geoFlagged ? (
              <span className="badge warning">GPS {event.distanceM} m away</span>
            ) : event.distanceM != null ? (
              <span className="muted">{event.distanceM} m from target</span>
            ) : null}
          </div>
          <p>{event.note ?? `${event.fromStatus ?? ""} → ${event.toStatus ?? ""}`}</p>
          {event.lat != null && event.lng != null ? (
            <a
              href={`https://www.google.com/maps?q=${event.lat},${event.lng}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Reported position on the map
            </a>
          ) : null}
        </div>
      ))}
    </Modal>
  );
}

function NewDeliveryModal({
  locations,
  prefillOrderId,
  onClose,
  onCreated,
}: {
  locations: Location[];
  prefillOrderId: string | null;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [orderId, setOrderId] = useState(prefillOrderId ?? "");
  const [locationId, setLocationId] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientNotes, setRecipientNotes] = useState("");
  const [date, setDate] = useState("");
  const [priceEur, setPriceEur] = useState("");
  const [distanceKm, setDistanceKm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const today = new Date();
        const to = new Date();
        to.setDate(today.getDate() + 30);
        const lists = await Promise.all(
          locations.map((loc) =>
            api.listCustomerOrders({
              locationId: loc.id,
              from: orderDayKey(today),
              to: orderDayKey(to),
              fulfillment: "COURIER",
            }),
          ),
        );
        setOrders(
          lists
            .flat()
            .filter(
              (order) =>
                !order.deliveryJob &&
                order.status !== "CANCELLED" &&
                order.status !== "COMPLETED",
            ),
        );
      } catch {
        setOrders([]);
      }
    })();
  }, [locations]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const price = Number(priceEur);
    if (!Number.isFinite(price) || price < 0) {
      setError("Delivery price must be a positive number.");
      return;
    }
    if (!deliveryAddress.trim()) {
      setError("Delivery address is required.");
      return;
    }
    if (!orderId && !locationId) {
      setError("Pick an order or a shop for an ad-hoc delivery.");
      return;
    }
    if (!orderId && !recipientName.trim()) {
      setError("Recipient name is required for ad-hoc deliveries.");
      return;
    }
    setBusy(true);
    try {
      await api.createDeliveryJob({
        orderId: orderId || undefined,
        locationId: orderId ? undefined : locationId,
        deliveryAddress: deliveryAddress.trim(),
        recipientName: recipientName.trim() || undefined,
        recipientPhone: recipientPhone.trim() || undefined,
        recipientNotes: recipientNotes.trim() || undefined,
        scheduledDate: date || undefined,
        distanceKm: distanceKm ? Number(distanceKm) : undefined,
        priceEur: price,
      });
      await onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not create the delivery.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New delivery" onClose={onClose}>
      {error ? <div className="alert error">{error}</div> : null}
      <form onSubmit={(event) => void submit(event)}>
        <div className="modal-form">
          <div className="field full">
            <label htmlFor="dispatch-order">From order</label>
            <select
              id="dispatch-order"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              disabled={busy}
            >
              <option value="">Ad-hoc delivery (no order)</option>
              {orders.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.code} · {order.customerName} ·{" "}
                  {new Date(order.scheduledDate).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
          {!orderId ? (
            <div className="field">
              <label htmlFor="dispatch-location">Pickup shop</label>
              <select
                id="dispatch-location"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={busy}
              >
                <option value="">— Select a shop —</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="field full">
            <label htmlFor="dispatch-address">Delivery address</label>
            <input
              id="dispatch-address"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              disabled={busy}
              required
              maxLength={500}
            />
          </div>
          <div className="field">
            <label htmlFor="dispatch-recipient">
              Recipient{orderId ? " (defaults to customer)" : ""}
            </label>
            <input
              id="dispatch-recipient"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              disabled={busy}
              maxLength={200}
            />
          </div>
          <div className="field">
            <label htmlFor="dispatch-phone">Phone</label>
            <input
              id="dispatch-phone"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value)}
              disabled={busy}
              maxLength={50}
            />
          </div>
          <div className="field">
            <label htmlFor="dispatch-date">
              Date{orderId ? " (defaults to order day)" : ""}
            </label>
            <input
              id="dispatch-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="field">
            <label htmlFor="dispatch-price">Delivery price (EUR)</label>
            <input
              id="dispatch-price"
              type="number"
              min="0"
              step="0.01"
              value={priceEur}
              onChange={(e) => setPriceEur(e.target.value)}
              disabled={busy}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="dispatch-distance">Distance (km)</label>
            <input
              id="dispatch-distance"
              type="number"
              min="0"
              step="0.01"
              value={distanceKm}
              onChange={(e) => setDistanceKm(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="field full">
            <label htmlFor="dispatch-notes">Notes for the courier</label>
            <textarea
              id="dispatch-notes"
              value={recipientNotes}
              onChange={(e) => setRecipientNotes(e.target.value)}
              disabled={busy}
              rows={2}
            />
          </div>
        </div>
        <div className="button-row" style={{ marginTop: "var(--space-3)" }}>
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy}>
            {busy ? "Creating…" : "Create delivery"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AssignModal({
  job,
  couriers,
  onClose,
  onAssigned,
}: {
  job: DeliveryJob;
  couriers: AssignableCourier[];
  onClose: () => void;
  onAssigned: () => Promise<void>;
}) {
  const [courierUserId, setCourierUserId] = useState("");
  const [distanceKm, setDistanceKm] = useState(job.distanceKm ?? "");
  const [overridePayoutEur, setOverridePayoutEur] = useState("");
  const [overrideCapacity, setOverrideCapacity] = useState(false);
  const [overrideSchedule, setOverrideSchedule] = useState(false);
  const [preview, setPreview] = useState<AssignmentPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OD-5: the selected courier's live carry state drives the "Assign anyway" gate.
  const selectedCourier = couriers.find((c) => c.id === courierUserId) ?? null;
  const atCapacity =
    selectedCourier?.maxConcurrentJobs != null &&
    selectedCourier.activeJobCount >= selectedCourier.maxConcurrentJobs;
  // OD-2: no CONFIRMED day today → the schedule soft-block needs an explicit
  // "Assign anyway" (the no-show signal made actionable).
  const offSchedule =
    selectedCourier != null &&
    selectedCourier.todayShift?.status !== "CONFIRMED";
  const scheduleLabel =
    selectedCourier == null
      ? ""
      : selectedCourier.todayShift == null
        ? "not scheduled today"
        : selectedCourier.todayShift.status === "PLANNED"
          ? "scheduled today but not confirmed yet"
          : selectedCourier.todayShift.status === "ENDED"
            ? "already ended their day"
            : selectedCourier.todayShift.status === "MISSED"
              ? "marked missed today"
              : "";

  useEffect(() => {
    if (!courierUserId) {
      setPreview(null);
      return;
    }
    let stale = false;
    void (async () => {
      try {
        const result = await api.previewDeliveryJobAssignment(job.id, {
          courierUserId,
          distanceKm: distanceKm ? Number(distanceKm) : undefined,
        });
        if (!stale) setPreview(result);
      } catch {
        if (!stale) setPreview(null);
      }
    })();
    return () => {
      stale = true;
    };
  }, [courierUserId, distanceKm, job.id]);

  const submit = async () => {
    if (!courierUserId) {
      setError("Choose a courier.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.assignDeliveryJob(job.id, {
        courierUserId,
        distanceKm: distanceKm ? Number(distanceKm) : undefined,
        overridePayoutEur: overridePayoutEur ? Number(overridePayoutEur) : undefined,
        overrideCapacity: atCapacity && overrideCapacity ? true : undefined,
        overrideSchedule: offSchedule && overrideSchedule ? true : undefined,
      });
      await onAssigned();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not assign the courier.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Assign ${job.code}`} onClose={onClose}>
      {error ? <div className="alert error">{error}</div> : null}
      <div className="modal-form">
        <div className="field full">
          <label htmlFor="assign-courier">Courier</label>
          <select
            id="assign-courier"
            value={courierUserId}
            onChange={(e) => setCourierUserId(e.target.value)}
            disabled={busy}
          >
            <option value="">— Select a courier —</option>
            {couriers.map((courier) => (
              <option key={courier.id} value={courier.id}>
                {courier.fullName}
                {courier.vehicleType ? ` (${courier.vehicleType.toLowerCase()})` : ""}
                {` — carrying ${courier.activeJobCount}${
                  courier.maxConcurrentJobs != null ? `/${courier.maxConcurrentJobs}` : ""
                }`}
                {courier.todayShift?.status === "CONFIRMED"
                  ? " — confirmed today"
                  : courier.todayShift?.status === "PLANNED"
                    ? " — planned, unconfirmed"
                    : " — not scheduled"}
                {courier.isAvailable ? "" : " — unavailable"}
              </option>
            ))}
          </select>
          {atCapacity ? (
            <label className="hint">
              <input
                type="checkbox"
                checked={overrideCapacity}
                onChange={(e) => setOverrideCapacity(e.target.checked)}
                disabled={busy}
              />
              Assign anyway — courier is at their vehicle cap (
              {selectedCourier?.activeJobCount}/{selectedCourier?.maxConcurrentJobs}); the
              override is recorded on the job.
            </label>
          ) : null}
          {offSchedule ? (
            <label className="hint">
              <input
                type="checkbox"
                checked={overrideSchedule}
                onChange={(e) => setOverrideSchedule(e.target.checked)}
                disabled={busy}
              />
              Assign anyway — courier is {scheduleLabel}; the exception is
              recorded on the job.
            </label>
          ) : null}
          {/* OD-30 / OD-101: assigning ahead of time stays allowed, with this
              warning — but the courier's PICKUP is refused until the shop
              records the payment. The courier never collects money. */}
          {/* bug_001 prevention: an unpriced order cannot book its sale at
              delivery — the field tap now records anyway, but dispatch should
              catch the gap while the goods are still in the shop. */}
          {job.order && job.order.balanceDueEur === null ? (
            <p className="hint" role="status">
              This order has NO agreed price. Delivery will be recorded, but
              the sale cannot be booked until someone sets the price.
            </p>
          ) : null}
          {job.order?.balanceDueEur && Number(job.order.balanceDueEur) > 0 ? (
            <p className="hint" role="status">
              This order still has {job.order.balanceDueEur} € unpaid
              (goods + delivery). The courier does not collect money, and the
              pickup is refused until the buyer&apos;s payment is recorded on
              the order.
            </p>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="assign-distance">Distance (km)</label>
          <input
            id="assign-distance"
            type="number"
            min="0"
            step="0.01"
            value={distanceKm}
            onChange={(e) => setDistanceKm(e.target.value)}
            disabled={busy}
          />
          {preview?.requiresDistance ? (
            <small className="hint">
              This courier is paid per km — distance is required.
            </small>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="assign-override">Payout override (EUR)</label>
          <input
            id="assign-override"
            type="number"
            min="0"
            step="0.01"
            value={overridePayoutEur}
            onChange={(e) => setOverridePayoutEur(e.target.value)}
            disabled={busy}
          />
          {preview && !preview.hasRule ? (
            <small className="hint">
              No payout rule applies — enter the payout manually.
            </small>
          ) : null}
        </div>
      </div>
      {preview?.payoutEur && !overridePayoutEur ? (
        <p style={{ marginTop: "var(--space-2)" }}>
          Courier payout: <strong>{preview.payoutEur} €</strong> · delivery margin:{" "}
          <strong>{preview.marginEur} €</strong>
        </p>
      ) : null}
      <div className="button-row" style={{ marginTop: "var(--space-3)" }}>
        <button type="button" className="button plain" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="button" onClick={() => void submit()} disabled={busy}>
          {busy ? "Assigning…" : "Assign"}
        </button>
      </div>
    </Modal>
  );
}
